import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth';
import { Kicker } from '@/components/Kicker';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      const dest = location.state?.from || (res.role === 'admin' ? '/admin' : '/portal');
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.status === 401 ? t('auth.errors.invalid') : t('auth.errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  const input = 'h-11 w-full rounded-lg border border-[#E4E9F0] bg-white px-3 text-sm outline-none focus:border-[#E2611C]';
  return (
    <PublicLayout hideFooter>
      <section className="pn-container py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_.8fr] items-start">
          <div>
            <Kicker>{t('brand.portal')}</Kicker>
            <h1 className="mt-3 font-display text-4xl font-semibold text-[#14171C]">{t('auth.login_title')}</h1>
            <p className="mt-3 max-w-md text-[15px] text-[#4A4D52]">{t('auth.login_subtitle')}</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F6F8FB] px-3 py-1.5 text-xs text-[#4A4D52]">
              <ShieldCheck className="h-4 w-4 text-[#1F7A4D]" />
              {t('auth.secure_hint')}
            </div>
          </div>
          <form onSubmit={onSubmit} className="pn-card p-6 md:p-8" data-testid="login-form">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('auth.email')}</label>
              <input required type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div className="mt-4">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('auth.password')}</label>
              <input required type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <button disabled={loading} type="submit" data-testid="login-submit-button" className="pn-btn-orange mt-6 w-full justify-center">
              {loading ? t('common.loading') : t('auth.submit')}
            </button>
            <div className="mt-4 text-center text-xs text-[#4A4D52]">
              {t('auth.admin_creates_accounts_hint')}
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
